import { useQuery } from '@tanstack/react-query';
import { billingApi, type EntitlementSummary } from '../services/api';

type TierFeature = keyof EntitlementSummary['features'];

/**
 * Workspace entitlement summary (tier, limits, feature flags). Shares the
 * ['billing','entitlements'] query key with BillingPage so it dedupes. Used to
 * gate feature surfaces (e.g. hide Pulse unless features.pulse).
 */
export function useEntitlements(options?: { enabled?: boolean }) {
  return useQuery({
    enabled: options?.enabled ?? true,
    queryKey: ['billing', 'entitlements'],
    queryFn: billingApi.getEntitlements,
    staleTime: 5 * 60 * 1000,
    // The MCP quota is spent in ChatGPT/Claude, not here, so nothing in the app
    // knows to invalidate this. Refetching on focus is what makes the
    // over-quota banner appear: the user tabs back to the portal *in order to*
    // upgrade, which is exactly the moment the number needs to be fresh.
    refetchOnWindowFocus: true,
  });
}

/**
 * Whether the current workspace has a given feature entitlement. Returns
 * `false` while loading, so gated surfaces stay hidden until confirmed (no flash
 * of a feature the user isn't entitled to).
 */
export function useHasFeature(feature: TierFeature): boolean {
  const { data } = useEntitlements();
  return !!data?.features?.[feature];
}
